import React, { useMemo, useContext, useState } from 'react';
import styled from 'styled-components';
import ModuleContext from 'CMFW/context/ModuleContext';
import ItemLayout from 'CMFW/layout/ItemLayout';
import { useForceRender } from 'hooks/useForceRender';
import { TranslationBankItemsModel } from 'models/TranslationBankItemsModel';
import { TranslationBankItemModel } from 'models/TranslationBankItemModel';
import { ContainerRight, EditorGapGreedy } from 'components/common/styles';
import TextInput from 'CMFW/input/TextInput';
import Translations from 'components/translation/Translations';
import { LoadingItem } from 'CMFW/layout/Loading';
import SaveButton from 'CMFW/button/SaveButton';
import DeleteButton from 'CMFW/button/DeleteButton';
import { EditorRow } from 'components/common/styles';
import { Button } from '@material-ui/core';
import MoveItemModal from '../../components/TranslationBank/MoveItemModal';
import CourseUsages from '../../components/TranslationBank/CourseUsages';

interface BankItemEditorProps {
  itemId: number;
}

const BankItemEditor: React.FC<BankItemEditorProps> = ({ itemId }) => {
  const forceRender = useForceRender();
  const itemsModel = useContext(ModuleContext) as TranslationBankItemsModel;
  const [moveModalOpen, setMoveModalOpen] = useState(false);

  // Create item model only when itemId > 0
  const itemModel = useMemo<TranslationBankItemModel | null>(() => {
    if (!itemId || itemId === 0) {
      return null;
    }

    // Get translationRefId from grid data
    const gridItem = itemsModel.gridData.find((item) => item.id === itemId);
    if (!gridItem) {
      console.error(`Translation bank item ${itemId} not found in grid data`);
      return null;
    }

    // Model constructor automatically calls loadItemEdit(itemId)
    const model = new TranslationBankItemModel({
      render: forceRender,
      itemId: itemId,
      ref: gridItem.translationRefId,
      itemsModel: itemsModel,
      onDelete: () => {
        itemsModel.itemId = 0;
        itemsModel.render();
      },
    });

    return model;
  }, [itemId, itemsModel, forceRender]);

  const handleMoveSuccess = () => {
    setMoveModalOpen(false);
    // Item moved out of this bank — clear selection
    if (itemModel?.onDelete) {
      itemModel.onDelete();
    }
  };

  return (
    <ContainerRight>
      {itemModel && (
        <ModuleContext.Provider value={itemModel}>
          <ItemLayout>
            <EditorRow>
              <EditorGapGreedy />
              <SaveButton />
            </EditorRow>
            <EditorRow>
              <TextInput keyName="notes" label="Notes" multiline rows={2} fullWidth size="small" />
            </EditorRow>
            <Scroll>
              <Translations keyName="sources" refId={itemModel.ref} />
              <CourseUsages usages={itemModel.itemUpdated?.courseUsages ?? []} />
            </Scroll>
            <EditorRow>
              <Button variant="outlined" size="small" onClick={() => setMoveModalOpen(true)}>
                Move
              </Button>
              <EditorGapGreedy />
              <DeleteButton />
            </EditorRow>
          </ItemLayout>
          <LoadingItem />

          {moveModalOpen && (
            <MoveItemModal
              itemId={itemId}
              itemsModel={itemsModel}
              onClose={() => setMoveModalOpen(false)}
              onSuccess={handleMoveSuccess}
            />
          )}
        </ModuleContext.Provider>
      )}
    </ContainerRight>
  );
};

export default BankItemEditor;

const Scroll = styled.div`
  height: 100%;
  overflow: auto;
`;
